import { create } from 'zustand';
import type { Transaction, TransactionType } from '@lira/shared';

/**
 * Orquestra a UI de lançamento (TypeSheet -> TransactionModal), disparável de
 * qualquer lugar (FAB do rail/bottom nav, cards de conta, estado vazio da lista).
 * O <TransactionComposer /> montado no AppShell lê este store e renderiza as superfícies.
 */
interface ComposeState {
  /** Seletor de tipo (popover no desktop / bottom sheet no mobile). */
  typeSheet: { open: boolean; anchor: DOMRect | null };
  /** Modal de lançamento. */
  modal: {
    open: boolean;
    type: TransactionType;
    editing: Transaction | null;
    presetAccountId: string | null;
    /** Modo "despesa no cartão" (M5): usa cartão em vez de conta, acento teal. */
    isCard: boolean;
    presetCardId: string | null;
  };

  openTypeSheet: (anchor?: DOMRect | null) => void;
  closeTypeSheet: () => void;

  /** Abre o modal limpo no tipo escolhido (opcionalmente com conta pré-selecionada). */
  openNew: (type: TransactionType, opts?: { accountId?: string }) => void;
  /** Abre o modal de despesa no cartão (opcionalmente com cartão pré-selecionado). */
  openCardExpense: (opts?: { cardId?: string }) => void;
  /** Abre o modal em modo edição para uma transação existente. */
  openEdit: (tx: Transaction) => void;
  closeModal: () => void;
}

export const useComposeStore = create<ComposeState>((set) => ({
  typeSheet: { open: false, anchor: null },
  modal: {
    open: false,
    type: 'expense',
    editing: null,
    presetAccountId: null,
    isCard: false,
    presetCardId: null,
  },

  openTypeSheet: (anchor = null) => set({ typeSheet: { open: true, anchor } }),
  closeTypeSheet: () => set((s) => ({ typeSheet: { ...s.typeSheet, open: false } })),

  openNew: (type, opts) =>
    set({
      typeSheet: { open: false, anchor: null },
      modal: {
        open: true,
        type,
        editing: null,
        presetAccountId: opts?.accountId ?? null,
        isCard: false,
        presetCardId: null,
      },
    }),

  openCardExpense: (opts) =>
    set({
      typeSheet: { open: false, anchor: null },
      modal: {
        open: true,
        type: 'expense',
        editing: null,
        presetAccountId: null,
        isCard: true,
        presetCardId: opts?.cardId ?? null,
      },
    }),

  openEdit: (tx) =>
    set({
      typeSheet: { open: false, anchor: null },
      modal: {
        open: true,
        type: tx.type,
        editing: tx,
        presetAccountId: null,
        isCard: !!tx.cardId,
        presetCardId: tx.cardId,
      },
    }),

  closeModal: () => set((s) => ({ modal: { ...s.modal, open: false } })),
}));
